import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { generatePPTV2, generatePPTV3 } from './action';
import { useAppDispatch } from 'src/hooks/redux';
import { useNavigate } from 'react-router-dom';

interface PresentationRightPanelProps {
  response: any;
  threadId: string;
  userId: any;
}

const PresentationRightPanel = ({ response, threadId, userId }: PresentationRightPanelProps) => {
   const params = new URLSearchParams(window.location.search);
  const agent = params.get("agent");
  const dispatch = useAppDispatch();
  const navigate = useNavigate()
  const [expandedPages, setExpandedPages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);


  // Toggle page accordion
  const togglePage = (pageId: string) => {
    setExpandedPages(prev =>
      prev.includes(pageId)
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  // Get color classes for categories
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      summary: 'bg-blue-100 text-blue-800 border-blue-300',
      background: 'bg-purple-100 text-purple-800 border-purple-300',
      analysis: 'bg-green-100 text-green-800 border-green-300',
      problem: 'bg-red-100 text-red-800 border-red-300',
      actions: 'bg-orange-100 text-orange-800 border-orange-300',
      appendix: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Handle Generate Deck
  const handleGenerateDeck = async () => {
    try {
      setLoading(true);
      if(agent === "qualitative-agent-v2"){
        const result: any = await dispatch(generatePPTV2({ userId, threadId })).unwrap();
     

        if (result?.workflowId) {
          navigate(result.endpoint)
        }
      } else {
        const result: any = await dispatch(generatePPTV3({ userId, threadId })).unwrap();
       

        if (result?.workflowId) {
          navigate(result.endpoint)
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to call generatePPT', err);
      setLoading(false);
    }
  };

  return (
    <div className="h-[90vh] py-4 border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 mb-2 border-b border-gray-400 px-4">
          <div className="w-24">
            <h1 className="text-sm font-bold">
              {response?.ppt_title || 'Presentation'}
            </h1>
          </div>

          <div>
            <button
              onClick={handleGenerateDeck}
              disabled={loading}
              className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center justify-center
                ${loading ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}
              `}
            >
              {loading ? (
                <span className="w-2 h-2 border-2 mx-3 my-1 border-gray-600 border-t-transparent rounded-full animate-spin"></span>
              ) : (
                'Generate Deck'
              )}
            </button>
          </div>
        </div>

        {/* Pages Grid */}
        <div className="grid gap-2 px-2">
          {response?.pages?.map((page: any) => {
            const isExpanded = expandedPages.includes(page?.page_id);

            return (
              <div
                key={page.page_id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all overflow-hidden"
              >
                {/* Page Header */}
                <button
                  onClick={() => togglePage(page.page_id)}
                  className="w-full p-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-left">
                      <h3 className="text-xs font-semibold text-gray-800">{page.title}</h3>
                      <p className="text-xs text-gray-600 mt-1">{page.purpose}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(page.metadata.category)}`}>
                      {page.metadata.category}
                    </span>
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </div>
                </button>

                {/* Page Details */}
                {isExpanded && (
                  <div className="px-2 pb-2 pt-2 bg-gray-50 border-t">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Priority</p>
                        <p className="text-sm font-medium">{page.metadata.priority}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Depth</p>
                        <p className="text-sm font-medium capitalize">{page.metadata.expected_depth}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Data Source</p>
                        <p className="text-sm font-medium">{page.metadata.data_source || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Features</p>
                        <div className="flex gap-2">
                          {page.metadata.requires_chart && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Chart</span>
                          )}
                          {page.metadata.auto_content_fit && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Auto-fit</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PresentationRightPanel;
